// @ts-nocheck
import { Box, Text, themeGet } from "@primer/react";
import React, { useState, useEffect } from "react";

import { LTAnchoredOverlay } from "@lambdatestincprivate/lt-components";
import {LTButton} from "@lambdatestincprivate/lt-components";
import {LTCheckbox} from "@lambdatestincprivate/lt-components";
import LTTextInput from "@lambdatestincprivate/lt-components/lib/components/LTInputBox";
import {LTLoader} from "@lambdatestincprivate/lt-components";
import LTCheckboxGroup from "./checkboxGroup.tsx";

import styled from "styled-components";

import { createUniqueID } from "../../utils/helper";
import {LTText} from "@lambdatestincprivate/lt-components";

const SelectPanelLoadingWrapper = styled.div`
	display: flex;
	flex: 1 0 auto;
	justify-content: center;
	align-items: center;
	padding: ${themeGet("space.3")};
`;

const ActionButtonGroup = styled.div`
	align-items: center;
	border-top: 1px solid ${themeGet("colors.border.lightGray")};
	display: flex;
	justify-content: end;
	padding: ${themeGet("space.2")};
	gap: ${themeGet("space.2")};
`;

const SelectPanelHeader = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${themeGet("space.1")};
	padding: ${themeGet("space.3")} 14px ${themeGet("space.3")} 14px;
`;

const SelectAllButtonWrapper = styled.div`
	background-color: ${themeGet("colors.bg.muted")};
	padding: ${themeGet("space.2")} ${themeGet("space.3")};
	border-top: 1px solid ${themeGet("colors.border.lightGray")};
	border-bottom: 1px solid ${themeGet("colors.border.lightGray")};
`;

export const LTSelectPanel: React.FC<any> = ({
	ariaLabel = undefined,
	buttonClassName = "",
	customButtonClassName = "",
	cancelButtonText = "Cancel",
	customHandlerButtonText = "Add New Extension",
	customHandlerFunc = null,
	customPanelButtonSize = LTButton.Size.MEDIUM,
	defaultValues = [],
	label = "",
	loading = false,
	name = "",
	options = [], //[{ id, label, value }]
	disabledOptions = [],
	onApply = () => {},
	onCancel = () => {},
	overlayContainerProps = {},
	subtitle = "",
	tabIndex,
	title = "",
}) => {
	const [closeOverlay, setCloseOverlay] = useState(false);
	const [searchedText, setSearchedText] = useState("");
	const [selectedOptions, setSelectedOptions] = useState([]);
	const [searchedOption, setSearchedOption] = useState([...options]);

	useEffect(() => {
		return () => {
			setCloseOverlay(false);
		};
	});

	useEffect(() => {
		setSelectedOptions(defaultValues || []);
	}, []);

	useEffect(() => {
		setSearchedOption(options);
		if (defaultValues.length === 0) {
			setSelectedOptions([]);
		}
	}, [options]);

	useEffect(() => {
		if (!searchedText) {
			setSearchedOption(options);
			return;
		}
		const filteredOptions = options?.filter(({ name }) =>
			name?.toLowerCase()?.includes(searchedText.toLowerCase())
		);
		setSearchedOption(filteredOptions);
	}, [searchedText]);

	const handleSearchInputChange = (value) => {
		setSearchedText(value);
	};

	const isApplyActive = () => {
		// Check if the arrays have the same length
		if (selectedOptions.length !== defaultValues.length) {
			return true;
		}

		// Convert all elements to strings to handle mixed types
		const sortedSelectedOptions = selectedOptions.map(String).sort();
		const sortedDefaultValues = defaultValues.map(String).sort();

		// Compare the sorted arrays
		for (let i = 0; i < sortedSelectedOptions.length; i++) {
			if (sortedSelectedOptions[i] !== sortedDefaultValues[i]) {
				return true;
			}
		}

		return false;
	};

	const handleSelectAllOptions = () => {
		if (selectedOptions.length !== options.length) {
			let allValues = searchedOption.map((element) => element.value);
			allValues = allValues.filter(element => !disabledOptions.includes(element));
			setSelectedOptions(allValues);
		} else {
			setSelectedOptions([]);
		}
	};

	const removeselectedOptions = () => {
		setSelectedOptions([]);
	};

	const onChange = (options) => {
		setSelectedOptions(options);
	};

	const renderElement = (element) => {
		const { name, value } = element;
		return {
			label: name,
			value: value.toString(),
			checked: selectedOptions.includes(value.toString()),
			...element,
		};
	};

	const clearSelectPanelData = () => {
		setSelectedOptions([]);
		setSearchedText("");
		onApply([]);
	};

	const anchorId = createUniqueID("lt-anchor");

	return (
		<>
			<Box id={anchorId} {...overlayContainerProps}>
				<SelectPanelHeader>
					<LTText
						as="h1"
						style={{
							fontWeight: "600",
							fontSize: "14px",
							lineHeight: "20px",
							margin: 0,
						}}
						variant={LTText.TextVariant.HEADER_BOLD}
					>
						{title}
					</LTText>
					<Text>{subtitle}</Text>
					<LTTextInput
						placeholder={`Search`}
						onChange={handleSearchInputChange}
						value={searchedText}
						trailingAction={() => handleSearchInputChange("")}
					/>
				</SelectPanelHeader>
				{loading ? (
					<SelectPanelLoadingWrapper>
						<LTLoader
							description={`Fetching ${name}`}
							size={LTLoader.SpinnerSizes.MEDIUM}
						/>
					</SelectPanelLoadingWrapper>
				) : (
					<>
						<SelectAllButtonWrapper>
							{searchedOption.length === 0 ? (
								"No Service Accounts exist."
							) : selectedOptions.length > 0 ? (
								<LTCheckbox
									indeterminate={
										selectedOptions.length > 0 &&
										selectedOptions.length !==
											options.length
									}
									onChange={() => removeselectedOptions()}
									checked={
										selectedOptions.length === options.length - disabledOptions.length
									}
									label={`${selectedOptions.length} Selected`}
								/>
							) : (
								<LTCheckbox
									label="Select All"
									checked={
										selectedOptions.length === options.length - disabledOptions.length
									}
									onChange={() => handleSelectAllOptions()}
								/>
							)}
						</SelectAllButtonWrapper>
						<Box
							sx={{
								maxHeight: "200px",
								overflow: "scroll",
								padding: "14px 16px",
							}}
						>
							<LTCheckboxGroup
								visuallyHidden={true}
								defaultSelected={selectedOptions}
								label={label}
								options={searchedOption?.map((element) =>
									renderElement(element)
								)}
								disabledOptions={disabledOptions}
								onChange={onChange}
							/>
						</Box>
						<ActionButtonGroup>
							{customHandlerFunc &&
								typeof customHandlerFunc === "function" && (
									<LTButton
										variant={LTButton.Variant.DEFAULT}
										onClick={() => {
											setCloseOverlay(true);
											customHandlerFunc();
										}}
										style={{ marginRight: "auto" }}
									>
										{customHandlerButtonText}
									</LTButton>
								)}
							<LTButton
								variant={LTButton.Variant.DEFAULT}
								onClick={() => {
									setCloseOverlay(true);
									onCancel();
								}}
								disabled={selectedOptions.length === 0}
							>
								{cancelButtonText}
							</LTButton>
							<LTButton
								variant={LTButton.Variant.PRIMARY}
								onClick={() => {
									onApply(selectedOptions);
									setCloseOverlay(true);
								}}
								disabled={!isApplyActive()}
							>
								Confirm
							</LTButton>
						</ActionButtonGroup>
					</>
				)}
			</Box>
		</>
	);
};

// export default LTSelectPanel;
